/** Pure chart maths, kept free of React for easy testing. */

/** Inclusive month-index window [from, to] of the visible range. */
export type IndexRange = readonly [number, number];

/**
 * [min, max] over every series within `range` (optionally including 0), padded so lines don't
 * touch the edges.
 */
export function valueDomain(
  series: readonly ArrayLike<number>[],
  {
    includeZero = true,
    padding = 0.05,
    range,
  }: { includeZero?: boolean; padding?: number; range?: IndexRange | undefined } = {},
): [number, number] {
  let min = includeZero ? 0 : Number.POSITIVE_INFINITY;
  let max = includeZero ? 0 : Number.NEGATIVE_INFINITY;
  for (const values of series) {
    const from = range ? Math.max(0, range[0]) : 0;
    const to = range ? Math.min(values.length - 1, range[1]) : values.length - 1;
    for (let i = from; i <= to; i++) {
      const v = values[i];
      if (v === undefined || !Number.isFinite(v)) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  if (min === max) {
    const pad = Math.abs(min) > 0 ? Math.abs(min) * 0.1 : 1;
    return [min - pad, max + pad];
  }
  const span = max - min;
  return [min < 0 || !includeZero ? min - span * padding : min, max + span * padding];
}

/** Index of the date nearest to `time` in an ascending array (binary search). */
export function nearestIndex(dates: readonly Date[], time: number): number {
  const n = dates.length;
  if (n === 0) return -1;
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((dates[mid] as Date).getTime() < time) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && time - (dates[lo - 1] as Date).getTime() < (dates[lo] as Date).getTime() - time)
    return lo - 1;
  return lo;
}

/** Reasonable number of x-axis ticks for a pixel width (≈ one per 90 px). */
export function tickCount(width: number, minSpacing = 90): number {
  return Math.max(2, Math.floor(width / minSpacing));
}

/**
 * Signed stacking: at each step positive values stack upwards from 0 and negative values stack
 * downwards from 0, in series order. Returns [y0, y1] bands per series.
 */
export function stackSigned(
  series: readonly ArrayLike<number>[],
  length: number,
): { y0: Float64Array; y1: Float64Array }[] {
  const up = new Float64Array(length);
  const down = new Float64Array(length);
  return series.map((values) => {
    const y0 = new Float64Array(length);
    const y1 = new Float64Array(length);
    for (let i = 0; i < length; i++) {
      const v = values[i] ?? 0;
      if (v >= 0) {
        y0[i] = up[i] ?? 0;
        up[i] = (up[i] ?? 0) + v;
        y1[i] = up[i] ?? 0;
      } else {
        y0[i] = down[i] ?? 0;
        down[i] = (down[i] ?? 0) + v;
        y1[i] = down[i] ?? 0;
      }
    }
    return { y0, y1 };
  });
}

/** Signed stack totals: [lowest, highest] per step (for the y-domain of stacked charts). */
export function stackExtent(
  series: readonly ArrayLike<number>[],
  length: number,
): [Float64Array, Float64Array] {
  const lo = new Float64Array(length);
  const hi = new Float64Array(length);
  for (const values of series) {
    for (let i = 0; i < length; i++) {
      const v = values[i] ?? 0;
      if (v >= 0) hi[i] = (hi[i] ?? 0) + v;
      else lo[i] = (lo[i] ?? 0) + v;
    }
  }
  return [lo, hi];
}

/** A calendar-year slice of the monthly axis: indexes [from, to] inclusive. */
export interface YearBucket {
  year: number;
  from: number;
  to: number;
  /** Fewer than 12 months (first/last year of the horizon). */
  partial: boolean;
}

/** Groups a monthly UTC axis into calendar years. */
export function yearBuckets(dates: readonly Date[]): YearBucket[] {
  const buckets: YearBucket[] = [];
  dates.forEach((date, i) => {
    const year = date.getUTCFullYear();
    const last = buckets.at(-1);
    if (last?.year === year) last.to = i;
    else buckets.push({ year, from: i, to: i, partial: false });
  });
  for (const b of buckets) b.partial = b.to - b.from + 1 < 12;
  return buckets;
}

/** Sum of values per bucket (flows). */
export function sumBuckets(
  values: ArrayLike<number>,
  buckets: readonly YearBucket[],
): Float64Array {
  return Float64Array.from(buckets, (b) => {
    let sum = 0;
    for (let i = b.from; i <= b.to; i++) sum += values[i] ?? 0;
    return sum;
  });
}

/** Last value per bucket (balances at year end). */
export function lastOfBuckets(
  values: ArrayLike<number>,
  buckets: readonly YearBucket[],
): Float64Array {
  return Float64Array.from(buckets, (b) => values[b.to] ?? 0);
}

/** Buckets overlapping an index range. */
export function bucketsInRange(
  buckets: readonly YearBucket[],
  range: IndexRange | undefined,
): YearBucket[] {
  if (!range) return [...buckets];
  return buckets.filter((b) => b.to >= range[0] && b.from <= range[1]);
}

export interface HistogramBin {
  from: number;
  to: number;
  count: number;
}

/**
 * Equal-width bins over [min, max] of `values` (non-finite values ignored). The last bin is
 * closed on both ends so the maximum is counted. A constant input gives one bin.
 */
export function histogramBins(values: Iterable<number>, bins: number): HistogramBin[] {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let finite = 0;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    finite++;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (finite === 0) return [];
  if (min === max) return [{ from: min, to: max, count: finite }];
  const n = Math.max(1, Math.floor(bins));
  const width = (max - min) / n;
  const result = Array.from({ length: n }, (_, i) => ({
    from: min + i * width,
    to: i === n - 1 ? max : min + (i + 1) * width,
    count: 0,
  }));
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    const bin = result[Math.min(n - 1, Math.floor((v - min) / width))];
    if (bin) bin.count++;
  }
  return result;
}

/** Minimum horizontal distance between marker labels, in pixels. */
const LABEL_GAP = 30;

/** Which marker labels to draw (left to right, skipping any that would overlap the last one). */
export function placeLabels(positions: readonly number[]): boolean[] {
  const order = positions.map((x, i) => ({ x, i })).sort((a, b) => a.x - b.x);
  const show = positions.map(() => false);
  let last = Number.NEGATIVE_INFINITY;
  for (const { x, i } of order) {
    if (x - last < LABEL_GAP) continue;
    show[i] = true;
    last = x;
  }
  return show;
}

/** Average advance of Inter's characters at 11 px. */
const CHAR_WIDTH_11 = 6.4;

/**
 * Approximate rendered width of a label, without measuring the DOM (works in workers too). At
 * 11 px it is exactly `length × 6.4`, as the screen charts' margins have always been computed.
 */
export function approxTextWidth(text: string, fontSize: number): number {
  return text.length * CHAR_WIDTH_11 * (fontSize / 11);
}

/**
 * Spreads label positions (e.g. y of direct labels at line ends) so neighbours are at least `gap`
 * apart, staying within [lo, hi] when there's room. Each label moves as little as it can and the
 * order is kept. Returns positions in the input order.
 */
export function spreadLabels(
  positions: readonly number[],
  gap: number,
  lo: number,
  hi: number,
): number[] {
  const order = positions.map((p, i) => ({ p, i })).sort((a, b) => a.p - b.p);
  const placed = order.map(({ p }) => Math.min(hi, Math.max(lo, p)));
  for (let k = 1; k < placed.length; k++) {
    placed[k] = Math.max(placed[k] as number, (placed[k - 1] as number) + gap);
  }
  const last = placed.length - 1;
  if (last >= 0 && (placed[last] as number) > hi) {
    placed[last] = hi;
    for (let k = last - 1; k >= 0; k--) {
      placed[k] = Math.min(placed[k] as number, (placed[k + 1] as number) - gap);
    }
  }
  const result = new Array<number>(positions.length);
  order.forEach(({ i }, k) => (result[i] = placed[k] as number));
  return result;
}

/** `EndLabels`: gap between the plot and the labels, and the colour key's length before each. */
export const END_LABEL_GAP = 6;
export const END_LABEL_KEY = 8;

/** Right margin that fits `EndLabels` with these texts. */
export function endLabelsWidth(texts: readonly string[], fontSize = 11): number {
  const longest = Math.max(0, ...texts.map((t) => approxTextWidth(t, fontSize)));
  return Math.ceil(END_LABEL_GAP + END_LABEL_KEY + 4 + longest + 2);
}

/** Space between a marker line and its label. */
export const MARKER_LABEL_PAD = 4;

export interface MarkerLabelPlacement {
  /** 0 is the row next to the plot, 1 the one above it. */
  row: number;
  /** `start`: the label runs right of its line; `end`: left of it (near the right edge). */
  anchor: 'start' | 'end';
}

/**
 * Places the labels of vertical markers (at `x`, `width` wide) in up to `rows` rows above the
 * plot, left to right, so none overlap. A label that fits nowhere is left out (null).
 */
export function placeMarkerLabels(
  markers: readonly { x: number; width: number }[],
  innerWidth: number,
  rows: number,
  gap = 8,
): (MarkerLabelPlacement | null)[] {
  const ends = new Array<number>(rows).fill(-Infinity);
  const out: (MarkerLabelPlacement | null)[] = markers.map(() => null);
  const order = markers
    .map((_, i) => i)
    .sort((a, b) => (markers[a]?.x ?? 0) - (markers[b]?.x ?? 0));
  for (const i of order) {
    const marker = markers[i];
    if (!marker) continue;
    const { x, width } = marker;
    const anchor = x + MARKER_LABEL_PAD + width <= innerWidth ? 'start' : 'end';
    const left = anchor === 'start' ? x + MARKER_LABEL_PAD : x - MARKER_LABEL_PAD - width;
    const row = ends.findIndex((end) => left >= end + gap);
    if (row < 0 || left < 0) continue;
    ends[row] = left + width;
    out[i] = { row, anchor };
  }
  return out;
}
